import React, { FC } from 'react';
import { useAsync } from 'react-use';

import { Icon } from '@/ui/Icon';
import { cn } from '@/utils';
import cnx from './style.module.sass';
import { backendApi } from '@/utils/api';
import IconSelector from '@/components/core/icons/IconSelector';
import { getFileSize } from '@/utils/getFileSize';

const getFile = async (id: string) => {
    if (!id) {
        return undefined;
    }

	try {
		const { data } = await backendApi.get<{
			url: string;
			name: string;
			size: number;
		}>(`v2/files/${id}`);
		return data;
	} catch (e) {
		console.error(e);
		return undefined;
	}
};

const decodeFileName = (fileName: string) =>
	!/[^\u0000-\u00ff]/.test(fileName) ? Buffer.from(fileName, 'latin1').toString('utf8') : fileName;

type FileItemProps = {
	id: string;
	onRemove?: () => void;
};

export const FileItem: FC<FileItemProps> = React.memo(({ id, onRemove }) => {
	const { value: file } = useAsync(async () => getFile(id), [id]);

	if (!id) {
		return null;
	}

	const filename = file?.name ? decodeFileName(file.name) : '';

	return (
		<div className={cn('flex items-center opacity-0 transition-opacity gap-2', { 'opacity-100': !!file })}>
			<div className="basis-11/12 pt-0.5 flex flex-row items-center gap-1">
				<span className={cnx.icon}>
					<IconSelector type="file" color="blue" />
				</span>
				<a title={filename} href={file?.url || '#'} target="_blank" download rel="noreferrer">
					{filename}
				</a>
				<span className={cnx.size}>({getFileSize(file?.size)} Мb)</span>
			</div>
			{onRemove !== undefined && (
				<button
					type="button"
					aria-label="Remove"
					className="flex items-center flex-1"
					onClick={() => onRemove()}
				>
					<Icon name="closeSmall" size={16} className="text-brand-red" />
				</button>
			)}
		</div>
	);
});
